import React from 'react';
import { isAxiosError } from 'axios';
import { createContext, useContext } from 'use-context-selector';

import { PAGE_HOST_KIND } from '~common';
import { LoadingScreen } from '~components/atoms/LoadingScreen';
import { TenantUnavailablePage } from '~components/pages/TenantUnavailablePage';
import { PAGE_HOST } from '~configs';
import { useQueryGetTenantInfo } from '~hooks/tenant/useQueryGetTenantInfo';
import { TenantInfo } from '~types';

type TenantContextType = {
  tenant: TenantInfo | null;
  isRefetching: boolean;
  refetch: () => void;
};

const TenantContext = createContext<TenantContextType>({
  tenant: null,
  isRefetching: false,
  refetch: () => null,
});

type Props = {
  children: React.ReactNode;
};

export const TenantProvider: React.FC<Props> = ({ children }) => {
  const tenantQuery = useQueryGetTenantInfo();
  const refetch = () => {
    tenantQuery.refetch();
  };

  if (PAGE_HOST.kind === PAGE_HOST_KIND.UNKNOWN) {
    return <TenantUnavailablePage />;
  }

  if (tenantQuery.isLoading) {
    return <LoadingScreen />;
  }

  const tenant = tenantQuery.data ?? null;

  if (PAGE_HOST.kind === PAGE_HOST_KIND.TENANT && !tenant?.isWhiteLabel) {
    const isNotFound =
      !!tenant ||
      (isAxiosError(tenantQuery.error) &&
        tenantQuery.error.response?.status === 404);

    return <TenantUnavailablePage onRetry={isNotFound ? undefined : refetch} />;
  }

  return (
    <TenantContext.Provider
      value={{ tenant, isRefetching: tenantQuery.isRefetching, refetch }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenantContext = () => useContext(TenantContext);
